import React, { useMemo, useRef, useState } from 'react';
import { Layout, Segment, Tabs } from 'tea-component';

import {
  FilterFixed,
  useFilterDataHook,
} from '@/pages/opinion/components/FilterCommon';
import { useIntl } from 'umi';
import '@op/assets/scss/pages/gamestore/index.scss';
import CardLoading from '@op/components/CardLoading';
import { localStoreSaveCountryToRegion } from '@op/utils';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import GameStorePoint from '@op/operationLog/pages/gameStore';
import ContentBody from '@op/components/ContentBody';
import ScoreTrendsConsole from '../widgets/ScoreTrendsPcConsole/ScoreTrendsConsole';
import { GameStoreConsoleScore } from '@op/widgets/GameStorePlate';
import useConsoleDevices from '@/pages/opinion/hooks/useConsoleDevices';
import './console.scss';
import CardCommon from '@/pages/opinion/components/CardCommon';
import useGameStoreTabs from '../hooks/useGameStoreTabs';
import XboxStore from '../components/Xbox';
import PlaystationStore from '../components/Playstation';

const { Content } = Layout;
const defaultChannel: Array<'opencritic' | 'metacritic'> = [
  'opencritic',
  'metacritic',
];
export default function GameStore({ gameInfo, userInfo }: any) {
  useInitBuriedPointInfo({ buriedPoint: GameStorePoint });
  const {
    loading: initLoading,
    filter,
    changeFilter,
    scrollBody,
    filterOptions,
  } = useFilterDataHook({
    gameInfo,
    hasHero: true,
    pageName: 'GameStore',
  });
  const hasXbox = useRef(false);

  const { formatMessage } = useIntl();

  const { tabOptions, deviceLoading, value, setValue } = useConsoleDevices({
    gameInfo,
  });

  const [originRegion, setOriginRegion] = useState([]);

  const params = useMemo(
    () => ({
      ...filter,
      device: value,
    }),
    [value, filter],
  );

  const { tabs, tab, onActive, relative, relativePlatform } = useGameStoreTabs({
    gameInfo,
  });

  const getRenderElement = () => {
    switch (tab) {
      case 'opencritic-metacritic':
        return (
          <>
            <FilterFixed
              filter={filterOptions}
              confirm={(value: any) => {
                changeFilter({
                  ...value,
                  ...gameInfo,
                });
              }}
              defaultConfig={{
                channel: {
                  channelKey: 'comments',
                },
              }}
              dealwithFilterOptionsMap={{
                channel: (options) => ({
                  ...(options || {}),
                  comments: ['metacritic', 'opencritic'],
                }),
              }}
              operateShowOptions={['history', 'more']}
              backCondOriginData={(value) => {
                const { region_country, channel } = value || {};
                setOriginRegion(region_country || []);
                localStoreSaveCountryToRegion(region_country);
                hasXbox.current =
                  channel?.comments?.includes?.('xbox') || false;
              }}
              beforeCacheDealwithData={() => false}
            ></FilterFixed>
            <CardCommon
              empty={tabOptions.length === 0}
              emptyHeight={560}
              loading={deviceLoading}
              loadingHeight={560}
              contentCard={tabOptions.length > 0}
            >
              <Segment
                className="segment-light card-slot-segment segment-box"
                value={value}
                onChange={(value) => {
                  setValue(value);
                }}
                options={tabOptions}
              />

              <CardLoading loading={initLoading || deviceLoading}>
                {/* 内部有6个UserScoreBox的时候加feeds-userscorebox--6，4个情况不加 */}
                <GameStoreConsoleScore
                  filter={params}
                  gameInfo={gameInfo}
                  channel={defaultChannel}
                  hasXbox={hasXbox.current}
                />
                <ScoreTrendsConsole
                  filter={params}
                  gameInfo={gameInfo}
                  channel={defaultChannel}
                  hasXbox={hasXbox.current}
                ></ScoreTrendsConsole>
              </CardLoading>
            </CardCommon>
          </>
        );
      case 'playstation':
        return (
          <PlaystationStore
            scrollBody={scrollBody}
            gameInfo={gameInfo}
          ></PlaystationStore>
        );
      default:
        return (
          <XboxStore scrollBody={scrollBody} gameInfo={gameInfo} channel={tab}></XboxStore>
        );
    }
  };

  return (
    <Content
      className="dbo-page dbo-gamestore dbo-gamestore-console-wrapper"
      ref={scrollBody}
    >
      <Content.Header
        title={formatMessage({ id: 'Game Store' })}
        className="dbo-header contentHead-normal"
      >
        <div className="header-tab">
          <Tabs
            ceiling
            tabs={tabs}
            animated={false}
            activeId={tab}
            onActive={(tab) => onActive(tab)}
          />
        </div>
      </Content.Header>
      <ContentBody>{getRenderElement()}</ContentBody>
    </Content>
  );
}
